import React, { useEffect, useState } from "react";
import {
  X,
  Hash,
  ShieldCheck,
  Plus,
  Save,
  RotateCcw,
  CreditCard,
} from "lucide-react";
import {
  useGetChargeTypeDetailsQuery,
  useUpdateChargeTypeMutation,
  useCreateChargeTypeMutation, // Assuming this exists in your billingApi
} from "../api/billingApi";

interface ChargeTypeModalProps {
  id?: number | null; // If null, we are in "Create" mode
  onClose: () => void;
  onSuccess: () => void;
}

export default function ChargeTypeModal({
  id,
  onClose,
  onSuccess,
}: ChargeTypeModalProps) {
  const isEdit = !!id;

  // Queries & Mutations
  const { data, isLoading } = useGetChargeTypeDetailsQuery(Number(id), {
    skip: !isEdit,
  });
  const [update, { isLoading: isUpdating }] = useUpdateChargeTypeMutation();
  const [create, { isLoading: isCreating }] = useCreateChargeTypeMutation();

  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "one_time" as "annual" | "one_time",
    description: "",
    is_active: true,
  });

  // Prefill form when editing
  useEffect(() => {
    if (isEdit && data) {
      setForm({
        name: data.name ?? "",
        code: data.code ?? "",
        type: (data.type as any) ?? "one_time",
        description: data.description ?? "",
        is_active: !!data.is_active,
      });
    } else if (!isEdit) {
      // Reset for New
      setForm({
        name: "",
        code: "",
        type: "one_time",
        description: "",
        is_active: true,
      });
    }
  }, [isEdit, data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...form, name: form.name.trim(), code: form.code.trim() };

    try {
      if (isEdit) {
        await update({ id: Number(id), body: payload }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onSuccess();
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  const isBusy = isUpdating || isCreating || (isEdit && isLoading);

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal Slide-over */}
      <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500">
        {/* Header Section */}
        <div className="p-8 border-b border-gray-50 bg-gray-50/30">
          <div className="flex justify-between items-start">
            <div className="flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center text-white shrink-0 shadow-lg">
                {isEdit ? (
                  <Hash className="w-6 h-6" />
                ) : (
                  <Plus className="w-6 h-6" />
                )}
              </div>
              <div>
                <div className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mb-1">
                  {isEdit ? "Modifying Entry" : "New Definition"}
                </div>
                <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
                  {isEdit
                    ? isLoading
                      ? "Syncing..."
                      : form.name
                    : "Create Charge"}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-3 bg-white border border-gray-100 rounded-2xl hover:bg-rose-50 hover:text-rose-600 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto p-8">
          <form
            id="charge-type-form"
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Formal Label
                </label>
                <input
                  className="w-full border-b-2 border-gray-100 py-2 text-sm font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  placeholder="e.g. Annual Membership"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Registry Code
                </label>
                <input
                  className="w-full border-b-2 border-gray-100 py-2 text-sm font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent disabled:opacity-40"
                  placeholder="ANNUAL_MEMB_01"
                  value={form.code}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, code: e.target.value }))
                  }
                  disabled={isEdit} // Often codes are immutable after creation
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Billing Logic
                </label>
                <select
                  className="w-full border-b-2 border-gray-100 py-2 text-[10px] font-black uppercase tracking-widest text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  value={form.type}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, type: e.target.value as any }))
                  }
                >
                  <option value="annual">Recurring Annual</option>
                  <option value="one_time">One-Time Event</option>
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <label className="flex items-center gap-3 cursor-pointer group pb-2">
                  <div
                    className={`w-10 h-5 rounded-full relative transition-all duration-300 ${
                      form.is_active ? "bg-emerald-500" : "bg-gray-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, is_active: e.target.checked }))
                      }
                    />
                    <div
                      className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-all duration-300 ${
                        form.is_active ? "translate-x-5" : ""
                      }`}
                    />
                  </div>
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900">
                    Status: {form.is_active ? "Active" : "Disabled"}
                  </span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Administrative Description
              </label>
              <textarea
                rows={4}
                className="w-full border-2 border-gray-50 rounded-2xl p-4 text-xs font-medium text-gray-600 outline-none focus:border-indigo-100 transition-all resize-none"
                placeholder="Internal notes regarding this fee structure..."
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
              />
            </div>
          </form>
        </div>

        {/* Action Footer */}
        <div className="p-8 border-t border-gray-50 bg-white">
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-3 h-3" /> Discard
            </button>
            <button
              type="submit"
              form="charge-type-form"
              disabled={isBusy}
              className="flex-[2] bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95 flex items-center justify-center gap-2"
            >
              {isBusy ? (
                "Processing..."
              ) : (
                <>
                  <Save className="w-4 h-4" />{" "}
                  {isEdit ? "Commit Changes" : "Create Node"}
                </>
              )}
            </button>
          </div>
          <p className="mt-4 text-center text-[9px] font-bold text-gray-300 uppercase tracking-widest">
            Accounting Security Protocol V.2.0 Active
          </p>
        </div>
      </div>
    </div>
  );
}
